/* MY GIFT bridge: connects the theme's gift builder (section-my-gift-builder.js) to the MY GIFT app proxy.
   Loaded with defer AFTER the builder script, so the builder's state and DOM are already initialised.
   Reads window.MYGIFT = { proxy: "/apps/gift", locale: "he" } (set by the section), fetches
   <proxy>/config and <proxy>/stock, rebuilds the amount chips from real stock, injects the extra fields
   the app needs, validates, and turns the final CTA into a POST <proxy>/checkout + redirect.
   2026-09-25: the phone is typed twice (number + confirmation), both formatted live as 05X-XXX-XXXX
   whatever the buyer types (+972, 00972, 05X), fields get a valid tick, the recipient language is a
   row of pills instead of a select. */
(function () {
  'use strict';

  var TIMEOUT_MS = 6000;
  var BUILDER_WAIT_TRIES = 40;
  var STYLE_ID = 'mygift-bridge-style';

  /* The strings needed before (or without) the config: "could not connect / try again" when the config
     itself is unreachable, and the two phone-pair messages. Everything else comes from config.texts
     (Settings > Page), which also overrides these when it carries the same key. */
  var FALLBACK_TEXTS = {
    he: {
      connectionErrorText: 'לא הצלחנו להתחבר. נסו שוב.',
      retryLabel: 'נסו שוב',
      phoneMismatchError: 'המספרים לא תואמים',
      phoneRetypeHint: 'הקלידו את המספר שוב, בלי להדביק',
      soldOutLabel: 'אזל',
      soldOutMessage: '₪{amount} אזל מהמלאי כרגע. בחרו סכום אחר.'
    },
    en: {
      connectionErrorText: 'We could not connect. Please try again.',
      retryLabel: 'Try again',
      phoneMismatchError: 'The numbers do not match',
      phoneRetypeHint: 'Please type the number again rather than pasting it',
      soldOutLabel: 'Sold out',
      soldOutMessage: '₪{amount} is sold out right now. Please pick another amount.'
    },
    ar: {
      connectionErrorText: 'تعذر الاتصال. حاول مرة أخرى.',
      retryLabel: 'حاول مرة أخرى',
      phoneMismatchError: 'الرقمان غير متطابقين',
      phoneRetypeHint: 'يرجى كتابة الرقم مرة أخرى بدلاً من لصقه',
      soldOutLabel: 'نفدت',
      soldOutMessage: '₪{amount} نفدت حالياً. يرجى اختيار مبلغ آخر.'
    }
  };

  var CSS =
    '.mygift-builder .mygift-bridge-error{color:#b60d28;display:block;font-size:11px;font-weight:400;line-height:1.5;margin-block-start:2px}' +
    '.mygift-builder .mygift-message.is-invalid textarea{border-color:#b60d28}' +
    '.mygift-builder .mygift-field-row.mygift-bridge-single{grid-template-columns:1fr}' +
    '.mygift-builder .mygift-full-field.mygift-bridge-mid{margin-block-end:10px}' +
    '.mygift-builder .mygift-full-field.mygift-bridge-date{margin-block-start:14px;margin-block-end:0}' +
    '.mygift-builder .mygift-field-row input[type="date"],.mygift-builder .mygift-full-field input[type="date"]{color:var(--mygift-ink);font:inherit;font-size:14px;font-weight:400}' +
    '.mygift-builder .mygift-amounts[data-mygift-ready]{grid-template-columns:repeat(auto-fit,minmax(96px,1fr))}' +
    '.mygift-builder .mygift-amounts button.is-soldout{background:#f4f5f7;border-style:dashed;color:var(--mygift-muted);cursor:not-allowed}' +
    '.mygift-builder .mygift-amounts button.is-soldout>span{text-decoration:line-through}' +
    '.mygift-builder .mygift-soldout-note{background:var(--mygift-soft-red);border-radius:10px;color:#b60d28;font-size:12px;line-height:1.5;margin:10px 0 0;padding:9px 12px}' +
    '.mygift-builder .mygift-amounts button small{display:block;font-size:10px;font-weight:400;margin-block-start:2px}' +
    '.mygift-builder .mygift-modes.mygift-bridge-single{grid-template-columns:1fr;max-width:460px}' +
    '.mygift-builder .mygift-lines{color:var(--mygift-muted);font-size:12px;line-height:1.7;list-style:none;margin:0 0 14px;padding:0}' +
    '.mygift-builder .mygift-lines li{padding-inline-start:14px;position:relative}' +
    '.mygift-builder .mygift-lines li::before{background:var(--mygift-red);border-radius:50%;content:"";height:6px;inset-inline-start:0;position:absolute;top:.7em;width:6px}' +
    '.mygift-builder .mygift-bridge-status{align-items:center;background:var(--mygift-soft-red);border:1px solid var(--mygift-red);border-radius:12px;color:#b60d28;display:flex;flex-wrap:wrap;font-size:13px;gap:12px;justify-content:space-between;line-height:1.5;margin-block-end:14px;padding:12px 16px}' +
    '.mygift-builder .mygift-bridge-status button{background:var(--mygift-red);border:0;border-radius:8px;color:#fff;font-size:12px;font-weight:700;min-height:34px;padding:0 14px}' +
    '.mygift-builder .mygift-primary[disabled]{cursor:wait;opacity:.6}' +
    '.mygift-builder .mygift-paused{color:var(--mygift-ink);font-size:17px;line-height:1.7;padding:64px 24px;text-align:center}' +
    '@media (max-width:560px){.mygift-builder .mygift-amounts[data-mygift-ready]{grid-template-columns:repeat(3,1fr)}}';

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  function readEnv() {
    var g = window.MYGIFT && typeof window.MYGIFT === 'object' ? window.MYGIFT : {};
    var proxy = typeof g.proxy === 'string' && g.proxy ? g.proxy : '/apps/gift';
    /* Always a same-origin path: strip any scheme or leading slashes so "//host/x" or "https://host/x"
       can never turn the config, stock and checkout calls into cross-origin requests. */
    proxy = proxy.replace(/^[a-z][a-z0-9+.-]*:/i, '').replace(/^\/+/, '').replace(/\/+$/, '');
    if (!proxy || proxy.indexOf('..') !== -1) proxy = 'apps/gift';
    proxy = '/' + proxy;
    var locale = String(g.locale || document.documentElement.getAttribute('lang') || 'he').slice(0, 2).toLowerCase();
    if (!/^[a-z]{2}$/.test(locale)) locale = 'he';
    return { proxy: proxy, locale: locale };
  }

  /* fetch + JSON with a 6 s AbortController timeout and a Content-Type check. Never throws. */
  function fetchJson(url, options) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, TIMEOUT_MS) : null;
    var opts = { credentials: 'same-origin', cache: 'no-store' };
    var key;
    options = options || {};
    for (key in options) if (Object.prototype.hasOwnProperty.call(options, key)) opts[key] = options[key];
    opts.headers = { Accept: 'application/json' };
    if (options.headers) for (key in options.headers) if (Object.prototype.hasOwnProperty.call(options.headers, key)) opts.headers[key] = options.headers[key];
    if (ctrl) opts.signal = ctrl.signal;

    return fetch(url, opts)
      .then(function (res) {
        var type = (res.headers.get('content-type') || '').toLowerCase();
        if (type.indexOf('application/json') === -1) {
          return { ok: false, status: res.status, data: null, error: 'content-type' };
        }
        return res.json().then(
          function (data) { return { ok: res.ok, status: res.status, data: data, error: null }; },
          function () { return { ok: false, status: res.status, data: null, error: 'parse' }; }
        );
      })
      .catch(function (err) {
        return { ok: false, status: 0, data: null, error: err && err.name === 'AbortError' ? 'timeout' : 'network' };
      })
      .then(function (result) {
        if (timer) clearTimeout(timer);
        return result;
      });
  }

  var ARABIC_INDIC = '٠١٢٣٤٥٦٧٨٩';
  var PERSIAN = '۰۱۲۳۴۵۶۷۸۹';
  function asciiDigits(s) {
    var out = '';
    var i, ch, a, p;
    for (i = 0; i < s.length; i++) {
      ch = s.charAt(i);
      a = ARABIC_INDIC.indexOf(ch);
      p = PERSIAN.indexOf(ch);
      out += a >= 0 ? String(a) : p >= 0 ? String(p) : ch;
    }
    return out;
  }

  function isEmail(v) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v);
  }

  /* ---------- phone numbers ----------
     Whatever the buyer types (+972 52..., 00972..., 972..., 052..., with spaces or dashes, Arabic-Indic
     digits included) is shown as 05X-XXX-XXXX, the split that makes a typo easy to see. An international
     prefix that is still being typed ("+97") is left alone until it is complete; a foreign number is
     left alone too and fails validation. */
  function formatPhone(raw) {
    var s = asciiDigits(String(raw || '')).replace(/[^\d+]/g, '');
    var plus = s.charAt(0) === '+';
    var d = s.replace(/\D/g, '');
    var intl = plus || d.indexOf('00') === 0 || d.indexOf('972') === 0;
    if (intl) {
      var m = /^(?:00)?972(\d*)$/.exec(d);
      if (!m) return (plus ? '+' : '') + d;
      d = m[1].replace(/^0/, '');
      if (!d) return (plus ? '+' : '') + '972';
      d = '0' + d;
    }
    d = d.slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return d.slice(0, 3) + '-' + d.slice(3);
    return d.slice(0, 3) + '-' + d.slice(3, 6) + '-' + d.slice(6);
  }
  /* "0528048064" for a complete or partial Israeli number; the bare digits of anything else. */
  function phoneDigits(raw) {
    return formatPhone(raw).replace(/\D/g, '');
  }
  /* Israeli mobile only: 05X + 8 digits. */
  function isPhone(v) {
    return /^05\d{8}$/.test(phoneDigits(v));
  }
  /* Rewrite the field's value in the display format, keeping the caret where the buyer is editing. */
  function applyPhoneFormat(input) {
    var raw = input.value;
    var formatted = formatPhone(raw);
    if (formatted === raw) return;
    var caret = null;
    try { caret = input.selectionStart; } catch (e) { caret = null; }
    var atEnd = caret === null || caret >= raw.length;
    var digitsBefore = atEnd ? null : asciiDigits(raw.slice(0, caret)).replace(/\D/g, '').length;
    input.value = formatted;
    if (digitsBefore === null) return;
    var pos = 0;
    var seen = 0;
    while (pos < formatted.length && seen < digitsBefore) {
      if (/\d/.test(formatted.charAt(pos))) seen++;
      pos++;
    }
    try { input.setSelectionRange(pos, pos); } catch (e) { /* not every input type allows it */ }
  }

  function pad2(n) { return n < 10 ? '0' + n : String(n); }
  function isoDate(d) { return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()); }
  function todayIso() { return isoDate(new Date()); }
  function addDaysIso(days) {
    var d = new Date();
    d.setDate(d.getDate() + days);
    return isoDate(d);
  }

  /* Only https, and only our own host (with or without a leading "www.") or a *.myshopify.com host.
     Anything else is refused: the app must never be able to send the buyer to a foreign site. */
  function safeCheckoutUrl(raw) {
    var url;
    if (typeof raw !== 'string' || !raw) return null;
    try { url = new URL(raw, location.href); } catch (e) { return null; }
    if (url.protocol !== 'https:') return null;
    if (url.username || url.password) return null;
    var hostname = url.hostname.toLowerCase();
    var own = location.hostname.toLowerCase();
    var bare = function (h) { return h.replace(/^www\./, ''); };
    if (hostname === own || bare(hostname) === bare(own)) return url.href;
    if (/^[a-z0-9-]+\.myshopify\.com$/.test(hostname)) return url.href;
    return null;
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = String(text);
    return node;
  }

  function initBridge(root) {
    if (root.dataset.mygiftBridge) return;
    root.dataset.mygiftBridge = 'true';
    ensureStyle();

    var env = readEnv();
    var q = function (sel, scope) { return (scope || root).querySelector(sel); };
    var qa = function (sel, scope) { return Array.prototype.slice.call((scope || root).querySelectorAll(sel)); };

    var copy = {};
    try {
      var copyNode = q('[data-mygift-copy]');
      if (copyNode) copy = JSON.parse(copyNode.textContent) || {};
    } catch (e) { copy = {}; }

    var els = {
      modes: q('.mygift-modes'),
      personalTab: q('[data-mode-btn="personal"]'),
      groupTab: q('[data-mode-btn="group"]'),
      flow: q('.mygift-flow'),
      p1: q('[data-panel="p1"]'),
      g1: q('[data-panel="g1"]'),
      p2: q('[data-panel="p2"]'),
      g2: q('[data-panel="g2"]'),
      s3: q('[data-panel="s3"]'),
      amounts: q('[data-mygift-amounts]') || q('.mygift-amounts'),
      customInput: q('[data-custom-amount]'),
      recipient: q('[data-panel="p1"] [data-input-recipient]') || q('[data-input-recipient]'),
      sender: q('[data-input-sender]'),
      phone: q('[data-panel="p1"] [data-input-phone]') || q('[data-panel="p1"] input[type="tel"]'),
      phoneConfirm: q('[data-panel="p1"] [data-input-phone-confirm]'),
      blessing: q('[data-blessing]'),
      blessingCount: q('[data-blessing-count]'),
      deliveryWrap: q('.mygift-delivery'),
      deliveryButtons: qa('[data-delivery]'),
      stepButtons: qa('[data-step-btn]'),
      actions: q('.mygift-actions'),
      cta: q('[data-mygift-cta]') || q('[data-primary]'),
      ctaLabel: q('[data-primary-label]'),
      lines: q('[data-mygift-lines]')
    };
    els.customWrap = els.customInput ? (els.customInput.closest('.mygift-custom-amount') || els.customInput.parentElement) : null;
    els.amountArea = els.amounts ? (els.amounts.closest('.mygift-amount-area') || els.amounts.parentElement) : null;

    if (!els.cta || !els.actions || !els.amounts) {
      console.warn('[mygift-bridge] builder markup not found, bridge not attached');
      return;
    }

    var state = {
      config: null,
      texts: {},
      fields: {},
      greetingMax: 180,
      stock: null,
      selected: null,
      originals: {},
      originalsCaptured: false,
      soldOutLocal: {},
      autoSelect: true,
      fieldsInjected: false,
      injected: {},
      dateWrap: null,
      statusBox: null,
      inFlight: false,
      suppressCustom: false,
      syncQueued: false,
      observer: null,
      retypeTimer: null,
      soldOutTimer: null
    };

    /* ---------- texts ---------- */
    function t(key) {
      var v = state.texts && state.texts[key];
      if (typeof v === 'string' && v !== '') return v;
      var fb = FALLBACK_TEXTS[env.locale] || FALLBACK_TEXTS.he;
      return typeof fb[key] === 'string' ? fb[key] : '';
    }

    /* ---------- builder introspection (no shared state object, so read the DOM) ---------- */
    function mode() {
      if (els.groupTab && els.groupTab.classList.contains('active')) return 'group';
      return 'personal';
    }
    function step() {
      if (els.s3 && !els.s3.hidden) return 3;
      if ((els.p2 && !els.p2.hidden) || (els.g2 && !els.g2.hidden)) return 2;
      return 1;
    }
    function goToStep(n) {
      var btn = null;
      els.stepButtons.forEach(function (b) { if (Number(b.dataset.stepBtn) === n) btn = b; });
      if (btn) btn.click();
    }
    function deliveryLater() {
      var later = false;
      els.deliveryButtons.forEach(function (b) {
        if (b.dataset.delivery === 'later' && b.classList.contains('active')) later = true;
      });
      return later;
    }
    function wrapOf(input) {
      if (!input) return null;
      return input.closest('label') || input.closest('[data-mygift-field]') || input.parentElement;
    }
    /* The row holding the phone and its confirmation (the section marks it), else the phone's own box. */
    function phoneRow() {
      if (!els.phone) return null;
      return els.phone.closest('.mygift-phone-row') || wrapOf(els.phone);
    }
    function val(input) {
      return input && typeof input.value === 'string' ? input.value.trim() : '';
    }

    /* ---------- inline errors, valid ticks and status ---------- */
    function setError(wrap, msg) {
      if (!wrap) return;
      var node = wrap.querySelector(':scope > .mygift-bridge-error');
      if (!msg) {
        if (node) node.parentNode.removeChild(node);
        wrap.classList.remove('is-invalid');
        return;
      }
      if (!node) {
        node = el('small', 'mygift-bridge-error');
        node.setAttribute('role', 'alert');
        wrap.appendChild(node);
      }
      node.textContent = msg;
      wrap.classList.add('is-invalid');
      wrap.classList.remove('is-valid');
    }
    function clearErrors() {
      qa('.mygift-bridge-error').forEach(function (n) { n.parentNode.removeChild(n); });
      qa('.is-invalid').forEach(function (n) { n.classList.remove('is-invalid'); });
    }
    function markValid(wrap, ok) {
      if (!wrap) return;
      wrap.classList.toggle('is-valid', !!ok && !wrap.classList.contains('is-invalid'));
    }
    /* Green ticks: a name once typed, a phone once complete, the confirmation once it matches. */
    function refreshValidity() {
      markValid(wrapOf(els.recipient), !!val(els.recipient));
      if (els.sender) markValid(wrapOf(els.sender), !!val(els.sender));
      var phone = val(els.phone);
      if (els.phone) markValid(wrapOf(els.phone), isPhone(phone));
      if (els.phoneConfirm) {
        var confirm = val(els.phoneConfirm);
        markValid(wrapOf(els.phoneConfirm), isPhone(phone) && isPhone(confirm) && phoneDigits(phone) === phoneDigits(confirm));
      }
      var rEmail = state.injected.recipientEmail;
      if (rEmail) markValid(wrapOf(rEmail), isEmail(val(rEmail)));
      var bEmail = state.injected.buyerEmail;
      if (bEmail) markValid(wrapOf(bEmail), isEmail(val(bEmail)));
    }
    function ensureStatusBox() {
      if (state.statusBox) return state.statusBox;
      var box = el('div', 'mygift-bridge-status');
      box.setAttribute('data-mygift-status', '');
      box.setAttribute('role', 'alert');
      box.hidden = true;
      box.appendChild(el('span'));
      var btn = el('button');
      btn.type = 'button';
      box.appendChild(btn);
      els.actions.parentNode.insertBefore(box, els.actions);
      state.statusBox = box;
      return box;
    }
    function showStatus(msg, retryFn) {
      var box = ensureStatusBox();
      box.querySelector('span').textContent = msg || t('connectionErrorText');
      var btn = box.querySelector('button');
      btn.textContent = t('retryLabel');
      btn.hidden = typeof retryFn !== 'function';
      btn.onclick = function () {
        hideStatus();
        if (typeof retryFn === 'function') retryFn();
      };
      box.hidden = false;
    }
    function hideStatus() {
      if (state.statusBox) state.statusBox.hidden = true;
    }
    function setBusy(busy) {
      state.inFlight = !!busy;
      els.cta.disabled = !!busy;
      els.cta.setAttribute('aria-busy', busy ? 'true' : 'false');
      els.cta.classList.toggle('is-loading', !!busy);
    }

    /* ---------- the phone pair ---------- */
    function checkConfirmNow() {
      if (!els.phone || !els.phoneConfirm) return;
      var phone = val(els.phone);
      var confirm = val(els.phoneConfirm);
      if (confirm && isPhone(phone) && isPhone(confirm) && phoneDigits(phone) !== phoneDigits(confirm)) {
        setError(wrapOf(els.phoneConfirm), t('phoneMismatchError'));
      }
    }
    function bindPhones() {
      var phones = [els.phone, els.phoneConfirm].filter(Boolean);
      /* The theme's custom.js formats every tel input site-wide as 05X-XXXXXXX from a document-level
         input listener that runs after ours. Re-apply the 3-3-4 split from a document listener of our
         own (registered later, so it runs later) and once more on the next tick, so ours is what stays. */
      document.addEventListener('input', function (e) {
        if (phones.indexOf(e.target) === -1) return;
        applyPhoneFormat(e.target);
        setTimeout(function () { applyPhoneFormat(e.target); refreshValidity(); }, 0);
      });
      phones.forEach(function (input) {
        input.addEventListener('input', function () {
          applyPhoneFormat(input);
          /* a change to the number retires a stale "do not match" on the confirmation */
          if (input === els.phone && els.phoneConfirm) setError(wrapOf(els.phoneConfirm), '');
          refreshValidity();
        });
        input.addEventListener('blur', function () {
          applyPhoneFormat(input);
          refreshValidity();
          checkConfirmNow();
        });
      });
      if (els.phoneConfirm) {
        /* The second copy exists to catch a typo, so it has to be typed, not pasted or dropped. */
        var block = function (e) {
          e.preventDefault();
          var wrap = wrapOf(els.phoneConfirm);
          setError(wrap, t('phoneRetypeHint'));
          clearTimeout(state.retypeTimer);
          state.retypeTimer = setTimeout(function () {
            var node = wrap && wrap.querySelector(':scope > .mygift-bridge-error');
            if (node && node.textContent === t('phoneRetypeHint')) setError(wrap, '');
          }, 2500);
        };
        els.phoneConfirm.addEventListener('paste', block);
        els.phoneConfirm.addEventListener('drop', block);
      }
    }

    /* ---------- amounts (chips from stock) ---------- */
    function denominations() {
      var list = state.stock && Array.isArray(state.stock.denominations) ? state.stock.denominations : [];
      return list
        .filter(function (d) { return d && typeof d === 'object' && (d.variantId || d.numericVariantId); })
        .map(function (d) {
          var face = Number(d.faceValueILS);
          return {
            variantId: d.variantId ? String(d.variantId) : '',
            numericVariantId: d.numericVariantId !== undefined && d.numericVariantId !== null ? String(d.numericVariantId) : '',
            faceValueILS: isFinite(face) ? face : 0,
            label: typeof d.label === 'string' && d.label ? d.label : '',
            soldOut: !!d.soldOut || !(Number(d.sellable) > 0)
          };
        })
        .sort(function (a, b) { return a.faceValueILS - b.faceValueILS; });
    }
    function chipKey(d) { return d.variantId || ('n:' + d.numericVariantId); }
    function isSoldOut(d) { return d.soldOut || !!state.soldOutLocal[chipKey(d)]; }

    function captureOriginals() {
      if (state.originalsCaptured) return;
      state.originalsCaptured = true;
      qa('button[data-amount]', els.amounts).forEach(function (b) {
        var n = Number(b.dataset.amount);
        if (isFinite(n)) state.originals[String(n)] = b;
      });
    }

    /* Drive the builder's own state through its existing DOM listeners so its preview follows:
       an original (now detached) button still fires its click handler; other amounts go through
       the custom-amount input's input event. */
    function driveBuilderAmount(amount) {
      var orig = state.originals[String(amount)];
      if (orig) { orig.click(); return; }
      if (!els.customInput) return;
      state.suppressCustom = true;
      try {
        els.customInput.value = String(amount);
        els.customInput.dispatchEvent(new Event('input', { bubbles: true }));
      } finally {
        state.suppressCustom = false;
      }
    }

    /* A sold-out chip stays clickable on purpose: tapping it explains instead of doing nothing. */
    function markSoldOut(btn) {
      btn.classList.remove('active');
      btn.classList.add('is-soldout');
      btn.setAttribute('aria-pressed', 'false');
      btn.setAttribute('aria-disabled', 'true');
      if (!btn.querySelector('small')) btn.appendChild(el('small', null, t('soldOutLabel')));
    }
    function showSoldOut(btn, amount) {
      if (!els.amountArea) return;
      var note = els.amountArea.querySelector('.mygift-soldout-note');
      if (!note) {
        note = el('p', 'mygift-soldout-note');
        note.setAttribute('role', 'status');
        if (els.amounts.nextSibling) els.amounts.parentNode.insertBefore(note, els.amounts.nextSibling);
        else els.amounts.parentNode.appendChild(note);
      }
      var text = t('soldOutMessage') || t('soldOutLabel');
      note.textContent = text.replace('{amount}', String(amount));
      note.hidden = false;
      shake(btn);
      clearTimeout(state.soldOutTimer);
      state.soldOutTimer = setTimeout(function () { note.hidden = true; }, 3500);
    }

    function selectDenomination(d) {
      state.selected = d ? { variantId: d.variantId, numericVariantId: d.numericVariantId, faceValueILS: d.faceValueILS } : null;
      qa('button[data-amount]', els.amounts).forEach(function (b) {
        var active = !!d && b.dataset.mygiftKey === chipKey(d);
        b.classList.toggle('active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      setError(els.amountArea, '');
      if (d) driveBuilderAmount(d.faceValueILS);
    }

    function onChipClick(e) {
      var btn = e.currentTarget;
      var key = btn.dataset.mygiftKey;
      var found = null;
      var soldOut = null;
      denominations().forEach(function (d) {
        if (chipKey(d) !== key) return;
        if (isSoldOut(d)) soldOut = d;
        else found = d;
      });
      hideStatus();
      if (found) selectDenomination(found);
      else if (soldOut || btn.classList.contains('is-soldout')) showSoldOut(btn, soldOut ? soldOut.faceValueILS : btn.dataset.amount);
    }

    function renderAmounts() {
      captureOriginals();
      var list = denominations();
      qa('button[data-amount]', els.amounts).forEach(function (b) { b.parentNode.removeChild(b); });

      list.forEach(function (d) {
        var b = el('button', 'mygift-bridge-chip');
        b.type = 'button';
        b.dataset.amount = String(d.faceValueILS);
        b.dataset.mygiftKey = chipKey(d);
        b.dataset.variantId = d.variantId;
        b.dataset.numericVariantId = d.numericVariantId;
        b.setAttribute('aria-pressed', 'false');
        b.appendChild(el('span', null, d.label || ('₪' + d.faceValueILS)));
        if (isSoldOut(d)) markSoldOut(b);
        b.addEventListener('click', onChipClick);
        if (els.customWrap && els.customWrap.parentNode === els.amounts) els.amounts.insertBefore(b, els.customWrap);
        else els.amounts.appendChild(b);
      });
      els.amounts.setAttribute('data-mygift-ready', '');

      var sellable = list.filter(function (d) { return !isSoldOut(d); });
      var pick = null;
      if (state.selected) {
        sellable.forEach(function (d) { if (chipKey(d) === chipKey(state.selected)) pick = d; });
      }
      if (!pick && state.autoSelect) {
        var def = Number(copy.defaultAmount);
        sellable.forEach(function (d) { if (!pick && d.faceValueILS === def) pick = d; });
        if (!pick && sellable.length) pick = sellable[0];
      }
      state.autoSelect = true;
      selectDenomination(pick);
    }

    /* A visible custom-amount input (config.hideCustomAmount = false): typing clears the chip selection;
       the typed value is resolved to a denomination with that face value at the next step. */
    function onCustomInput() {
      if (state.suppressCustom) return;
      state.selected = null;
      qa('button[data-amount]', els.amounts).forEach(function (b) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      setError(els.amountArea, '');
      hideStatus();
    }
    function resolveCustomAmount() {
      if (!els.customInput || !els.customWrap || els.customWrap.hidden) return null;
      var n = Number(asciiDigits(val(els.customInput)).replace(/[^\d.]/g, ''));
      if (!isFinite(n) || n <= 0) return null;
      var found = null;
      denominations().forEach(function (d) { if (!found && !isSoldOut(d) && d.faceValueILS === n) found = d; });
      return found;
    }

    /* ---------- field injection ---------- */
    function makeInput(name, label, type) {
      var wrap = el('label', 'mygift-field');
      wrap.dataset.mygiftField = name;
      wrap.appendChild(el('span', 'mygift-field-label', label));
      var input = el('input');
      input.type = type;
      input.name = name;
      input.dataset.mygiftInput = name;
      if (type === 'email') {
        input.autocomplete = 'email';
        input.setAttribute('inputmode', 'email');
        input.setAttribute('spellcheck', 'false');
        input.setAttribute('autocapitalize', 'off');
      }
      wrap.appendChild(input);
      state.injected[name] = input;
      return wrap;
    }
    function makeSelect(name, label, options, value) {
      var wrap = el('label', 'mygift-field');
      wrap.dataset.mygiftField = name;
      wrap.appendChild(el('span', 'mygift-field-label', label));
      var select = el('select');
      select.name = name;
      select.dataset.mygiftInput = name;
      options.forEach(function (o) {
        var opt = el('option', null, o.label);
        opt.value = o.value;
        select.appendChild(opt);
      });
      if (value !== undefined) select.value = value;
      wrap.appendChild(select);
      state.injected[name] = select;
      return wrap;
    }
    /* A row of pills with a hidden input carrying the choice (val() and the error mapping treat it like any field). */
    function makePills(name, label, options, value) {
      var wrap = el('div', 'mygift-field mygift-pills');
      wrap.dataset.mygiftField = name;
      wrap.appendChild(el('span', 'mygift-field-label', label));
      var group = el('div', 'mygift-pill-group');
      group.setAttribute('role', 'group');
      group.setAttribute('aria-label', label);
      var hidden = el('input');
      hidden.type = 'hidden';
      hidden.name = name;
      hidden.value = value === undefined || value === null ? '' : String(value);
      hidden.dataset.mygiftInput = name;
      options.forEach(function (o) {
        var b = el('button', null, o.label);
        b.type = 'button';
        b.dataset.value = o.value;
        var on = o.value === hidden.value;
        b.classList.toggle('active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.addEventListener('click', function () {
          hidden.value = o.value;
          qa('button', group).forEach(function (x) {
            var active = x === b;
            x.classList.toggle('active', active);
            x.setAttribute('aria-pressed', active ? 'true' : 'false');
          });
          hidden.dispatchEvent(new Event('change', { bubbles: true }));
        });
        group.appendChild(b);
      });
      wrap.appendChild(group);
      wrap.appendChild(hidden);
      state.injected[name] = hidden;
      return wrap;
    }
    function placeRow(fields, before, parent) {
      if (!fields.length) return;
      var node;
      if (fields.length === 1) {
        node = fields[0];
        node.classList.add('mygift-full-field', 'mygift-bridge-mid');
      } else {
        node = el('div', 'mygift-field-row');
        fields.forEach(function (f) { node.appendChild(f); });
      }
      if (before && before.parentNode === parent) parent.insertBefore(node, before);
      else parent.appendChild(node);
    }
    function languageOptions() {
      var raw = state.config && state.config.languages;
      var out = [];
      if (Array.isArray(raw)) {
        raw.forEach(function (l) {
          if (typeof l === 'string' && l) out.push({ value: l, label: l });
          else if (l && typeof l === 'object' && l.code && l.enabled !== false) out.push({ value: String(l.code), label: String(l.label || l.code) });
        });
      } else if (raw && typeof raw === 'object') {
        Object.keys(raw).forEach(function (k) { out.push({ value: k, label: String(raw[k]) }); });
      }
      return out;
    }
    function occasionOptions() {
      var raw = state.config && state.config.occasions;
      var defLang = (state.config && state.config.defaultLanguage) || env.locale;
      if (!Array.isArray(raw)) return [];
      return raw
        .filter(function (o) { return o && typeof o === 'object' && o.key && o.enabled !== false; })
        .sort(function (a, b) { return (Number(a.order) || 0) - (Number(b.order) || 0); })
        .map(function (o) {
          var labels = o.labels && typeof o.labels === 'object' ? o.labels : {};
          var first = Object.keys(labels).length ? labels[Object.keys(labels)[0]] : '';
          return { value: String(o.key), label: String(labels[env.locale] || labels[defLang] || first || o.key) };
        });
    }
    function fieldMode(name) {
      var m = state.fields[name];
      return m === 'required' || m === 'optional' || m === 'hidden' ? m : 'optional';
    }

    /* The section renders an SMS / email toggle and owns which one is active
       (SMS by default). Everything below treats the channel the shopper did not
       pick as hidden, so it is never required and never sent. */
    function channel() {
      var active = q('[data-mygift-channel] button.active');
      var name = active && active.dataset ? active.dataset.channel : '';
      return name === 'email' ? 'email' : 'sms';
    }
    function channelMode(name) {
      var mode = fieldMode(name);
      if (mode === 'hidden') return 'hidden';
      if (name === 'recipientPhone') return channel() === 'sms' ? 'required' : 'hidden';
      if (name === 'recipientEmail') return channel() === 'email' ? 'required' : 'hidden';
      return mode;
    }
    function syncChannel() {
      var row = phoneRow();
      if (row) row.hidden = channelMode('recipientPhone') === 'hidden';
      var emailWrap = wrapOf(state.injected.recipientEmail);
      if (emailWrap) emailWrap.hidden = channelMode('recipientEmail') === 'hidden';
      var toggle = q('[data-mygift-channel]');
      /* With one of the two switched off in the app config there is nothing to
         choose, so the toggle goes away and the surviving field just shows. */
      if (toggle) toggle.hidden = fieldMode('recipientPhone') === 'hidden' || fieldMode('recipientEmail') === 'hidden';
      var first = emailWrap && emailWrap.parentNode ? emailWrap : row;
      if (toggle && first && first.parentNode && toggle.parentNode === first.parentNode && toggle.nextSibling !== first) {
        first.parentNode.insertBefore(toggle, first);
      }
    }

    function injectFields() {
      if (state.fieldsInjected) return;
      state.fieldsInjected = true;

      var recipientWrap = wrapOf(els.recipient);
      if (recipientWrap) recipientWrap.dataset.mygiftField = 'recipientName';
      var senderWrap = wrapOf(els.sender);
      if (senderWrap) {
        senderWrap.dataset.mygiftField = 'senderName';
        senderWrap.hidden = fieldMode('senderName') === 'hidden';
        var row1 = senderWrap.closest('.mygift-field-row');
        if (row1) row1.classList.toggle('mygift-bridge-single', senderWrap.hidden);
      }
      var phoneWrap = wrapOf(els.phone);
      if (phoneWrap) phoneWrap.dataset.mygiftField = 'recipientPhone';
      var confirmWrap = wrapOf(els.phoneConfirm);
      if (confirmWrap) confirmWrap.dataset.mygiftField = 'recipientPhoneConfirm';
      var row = phoneRow();
      if (row) row.hidden = fieldMode('recipientPhone') === 'hidden';

      if (els.p1) {
        var recipientEmailWrap = null;
        if (fieldMode('recipientEmail') !== 'hidden') {
          recipientEmailWrap = makeInput('recipientEmail', t('recipientEmailLabel'), 'email');
          recipientEmailWrap.setAttribute('data-channel-field', 'email');
        }
        var emails = [];
        if (fieldMode('buyerEmail') !== 'hidden') emails.push(makeInput('buyerEmail', t('buyerEmailLabel'), 'email'));

        var extras = [];
        var langs = languageOptions();
        if (fieldMode('recipientLanguage') !== 'hidden' && langs.length) {
          var defLang = (state.config && state.config.defaultLanguage) || env.locale;
          var initial = langs.some(function (o) { return o.value === env.locale; }) ? env.locale : defLang;
          extras.push(makePills('recipientLang', t('recipientLanguageLabel'), langs, initial));
        }
        var occ = occasionOptions();
        if (fieldMode('occasion') !== 'hidden' && occ.length) {
          var occLabelNode = q('.mygift-occasions');
          var occLabel = t('occasionLabel') || (occLabelNode && occLabelNode.getAttribute('aria-label')) || '';
          extras.push(makeSelect('occasion', occLabel, [{ value: '', label: '' }].concat(occ), ''));
        }

        var rowInPanel = row && row.parentNode === els.p1 ? row : null;
        /* the email field lands right before the phone row: the two channel fields stay
           together under the toggle that switches them */
        if (recipientEmailWrap) placeRow([recipientEmailWrap], rowInPanel, els.p1);
        /* language and occasion after the phone pair, the buyer's email last */
        var after = rowInPanel ? rowInPanel.nextSibling : null;
        extras.forEach(function (field) { placeRow([field], after, els.p1); });
        placeRow(emails, after, els.p1);
      }

      var laterBtn = null;
      els.deliveryButtons.forEach(function (b) { if (b.dataset.delivery === 'later') laterBtn = b; });
      if (fieldMode('deliveryDate') === 'hidden') {
        if (laterBtn) laterBtn.hidden = true;
        if (els.deliveryWrap) els.deliveryWrap.classList.add('mygift-bridge-single');
      } else if (els.deliveryWrap) {
        var dateWrap = makeInput('deliverAt', t('deliveryDateLabel'), 'date');
        dateWrap.classList.add('mygift-full-field', 'mygift-bridge-date');
        var dateInput = state.injected.deliverAt;
        dateInput.min = todayIso();
        var maxDays = Number(state.config && state.config.maxDaysAhead);
        if (isFinite(maxDays) && maxDays > 0) dateInput.max = addDaysIso(maxDays);
        dateWrap.hidden = !deliveryLater();
        els.deliveryWrap.parentNode.insertBefore(dateWrap, els.deliveryWrap.nextSibling);
        state.dateWrap = dateWrap;
      }

      syncChannel();
      document.addEventListener('mygift:channel', syncChannel);
      refreshValidity();
    }

    function renderLines() {
      if (!els.lines) return;
      var arr = Array.isArray(state.texts.linesAboveCta) ? state.texts.linesAboveCta : [];
      arr = arr.filter(function (s) { return typeof s === 'string' && s.trim() !== ''; });
      while (els.lines.firstChild) els.lines.removeChild(els.lines.firstChild);
      if (arr.length) {
        var ul = el('ul', 'mygift-lines');
        arr.forEach(function (s) { ul.appendChild(el('li', null, s)); });
        els.lines.appendChild(ul);
      }
    }

    /* The store-only pill under the card: the section carries the theme text, the dashboard text replaces it. */
    function renderStoreOnly() {
      var note = q('[data-store-only]');
      if (!note) return;
      var text = state.texts.storeOnlyNote;
      if (typeof text !== 'string' || !text.trim()) return;
      var span = note.querySelector('span') || note;
      span.textContent = text.trim();
    }

    function renderPaused() {
      if (state.observer) { state.observer.disconnect(); state.observer = null; }
      if (els.modes) els.modes.hidden = true;
      if (!els.flow) return;
      while (els.flow.firstChild) els.flow.removeChild(els.flow.firstChild);
      var note = el('div', 'mygift-paused', t('sellingPausedMessage'));
      note.setAttribute('role', 'status');
      els.flow.appendChild(note);
    }

    /* ---------- keep our bits in step with the builder's own render() ---------- */
    function syncView() {
      state.syncQueued = false;
      var s = step();
      var checkoutStep = s === 3 && mode() === 'personal';

      if (els.lines) {
        var wantLines = checkoutStep && els.lines.childNodes.length > 0;
        if (els.lines.hidden !== !wantLines) els.lines.hidden = !wantLines;
      }
      if (els.ctaLabel && checkoutStep && state.config) {
        var label = t('ctaLabel');
        if (label && els.ctaLabel.textContent !== label) els.ctaLabel.textContent = label;
      }
      if (els.blessingCount && els.blessing && state.config && state.greetingMax !== 180) {
        var want = els.blessing.value.length + '/' + state.greetingMax;
        if (els.blessingCount.textContent !== want) els.blessingCount.textContent = want;
      }
      if (state.dateWrap) {
        var later = deliveryLater();
        if (state.dateWrap.hidden !== !later) state.dateWrap.hidden = !later;
      }
    }
    /* Coalesce bursts of mutations into one sync. A microtask (not requestAnimationFrame) so it also
       runs in background tabs; syncView only writes when something differs, so it settles. */
    function scheduleSync() {
      if (state.syncQueued) return;
      state.syncQueued = true;
      Promise.resolve().then(syncView);
    }
    function observe() {
      if (state.observer || typeof MutationObserver !== 'function') return;
      state.observer = new MutationObserver(scheduleSync);
      state.observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'class'] });
    }

    /* ---------- validation ---------- */
    function validate(scope) {
      var errs = [];
      var all = scope === 'all';
      function push(wrap, input, msg, stepNo) {
        if (!wrap) return;
        errs.push({ wrap: wrap, input: input, msg: msg, step: stepNo });
      }
      function text(name, input, forced) {
        var m = forced || fieldMode(name);
        if (m === 'hidden' || !input) return { mode: 'hidden', value: '' };
        return { mode: m, value: val(input) };
      }

      if (all || scope === 'p1') {
        if (!val(els.recipient)) push(wrapOf(els.recipient), els.recipient, t('requiredError'), 1);

        var sender = text('senderName', els.sender);
        if (sender.mode === 'required' && !sender.value) push(wrapOf(els.sender), els.sender, t('requiredError'), 1);

        var rEmail = text('recipientEmail', state.injected.recipientEmail, channelMode('recipientEmail'));
        if (rEmail.mode === 'required' && !rEmail.value) push(wrapOf(state.injected.recipientEmail), state.injected.recipientEmail, t('requiredError'), 1);
        else if (rEmail.value && !isEmail(rEmail.value)) push(wrapOf(state.injected.recipientEmail), state.injected.recipientEmail, t('invalidEmailError'), 1);

        var bEmail = text('buyerEmail', state.injected.buyerEmail);
        if (bEmail.mode === 'required' && !bEmail.value) push(wrapOf(state.injected.buyerEmail), state.injected.buyerEmail, t('requiredError'), 1);
        else if (bEmail.value && !isEmail(bEmail.value)) push(wrapOf(state.injected.buyerEmail), state.injected.buyerEmail, t('invalidEmailError'), 1);

        var phone = text('recipientPhone', els.phone, channelMode('recipientPhone'));
        if (phone.mode === 'required' && !phone.value) push(wrapOf(els.phone), els.phone, t('requiredError'), 1);
        else if (phone.value && !isPhone(phone.value)) push(wrapOf(els.phone), els.phone, t('invalidPhoneError'), 1);
        else if (phone.mode !== 'hidden' && els.phoneConfirm && (phone.value || phone.mode === 'required')) {
          /* the second copy: present, and the same number once both are normalised */
          var confirm = val(els.phoneConfirm);
          if (!confirm) push(wrapOf(els.phoneConfirm), els.phoneConfirm, t('requiredError'), 1);
          else if (phoneDigits(confirm) !== phoneDigits(phone.value)) push(wrapOf(els.phoneConfirm), els.phoneConfirm, t('phoneMismatchError'), 1);
        }

        var lang = text('recipientLanguage', state.injected.recipientLang);
        if (lang.mode === 'required' && !lang.value) push(wrapOf(state.injected.recipientLang), state.injected.recipientLang, t('requiredError'), 1);

        var occ = text('occasion', state.injected.occasion);
        if (occ.mode === 'required' && !occ.value) push(wrapOf(state.injected.occasion), state.injected.occasion, t('requiredError'), 1);
      }

      if (all || scope === 'p2') {
        if (!state.selected) {
          var custom = resolveCustomAmount();
          if (custom) selectDenomination(custom);
          else push(els.amountArea, null, t('requiredError'), 2);
        }
      }

      if (all || scope === 's3') {
        if (els.blessing && els.blessing.value.length > state.greetingMax) {
          els.blessing.value = els.blessing.value.slice(0, state.greetingMax);
          els.blessing.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (state.dateWrap && deliveryLater()) {
          var input = state.injected.deliverAt;
          var v = val(input);
          var ok = /^\d{4}-\d{2}-\d{2}$/.test(v) && v >= todayIso() && (!input.max || v <= input.max);
          if (!v) push(state.dateWrap, input, t('requiredError'), 3);
          else if (!ok) push(state.dateWrap, input, t('invalidDateError') || t('requiredError'), 3);
        }
      }
      return errs;
    }

    function shake(wrap) {
      if (!wrap) return;
      wrap.classList.remove('mygift-shake');
      void wrap.offsetWidth;
      wrap.classList.add('mygift-shake');
    }

    function showErrors(errs) {
      clearErrors();
      if (!errs.length) return;
      errs.forEach(function (e) { setError(e.wrap, e.msg); shake(e.wrap); });
      var first = errs[0];
      if (first.step && first.step !== step()) goToStep(first.step);
      var focusTarget = first.input && first.input.type !== 'hidden' ? first.input : null;
      if (focusTarget && typeof focusTarget.focus === 'function') {
        try { focusTarget.focus({ preventScroll: false }); } catch (e) { /* ignore */ }
      } else if (first.wrap && typeof first.wrap.scrollIntoView === 'function') {
        first.wrap.scrollIntoView({ block: 'center' });
      }
    }

    function mapServerErrors(errors) {
      var out = [];
      var leftovers = [];
      var map = {
        recipientName: { wrap: wrapOf(els.recipient), input: els.recipient, step: 1 },
        senderName: { wrap: wrapOf(els.sender), input: els.sender, step: 1 },
        buyerName: { wrap: wrapOf(els.sender), input: els.sender, step: 1 },
        recipientEmail: { wrap: wrapOf(state.injected.recipientEmail), input: state.injected.recipientEmail, step: 1 },
        buyerEmail: { wrap: wrapOf(state.injected.buyerEmail), input: state.injected.buyerEmail, step: 1 },
        recipientPhone: { wrap: wrapOf(els.phone), input: els.phone, step: 1 },
        recipientPhoneConfirm: { wrap: wrapOf(els.phoneConfirm), input: els.phoneConfirm, step: 1 },
        recipientLang: { wrap: wrapOf(state.injected.recipientLang), input: state.injected.recipientLang, step: 1 },
        occasion: { wrap: wrapOf(state.injected.occasion), input: state.injected.occasion, step: 1 },
        greeting: { wrap: wrapOf(els.blessing), input: els.blessing, step: 3 },
        deliverAt: { wrap: state.dateWrap, input: state.injected.deliverAt, step: 3 },
        variantId: { wrap: els.amountArea, input: null, step: 2 },
        numericVariantId: { wrap: els.amountArea, input: null, step: 2 },
        amount: { wrap: els.amountArea, input: null, step: 2 },
        stock: { wrap: els.amountArea, input: null, step: 2 }
      };
      Object.keys(errors).forEach(function (key) {
        var raw = errors[key];
        var msg = typeof raw === 'string' && raw ? raw : Array.isArray(raw) && typeof raw[0] === 'string' ? raw[0] : t('requiredError');
        var target = map[key];
        if (target && target.wrap) out.push({ wrap: target.wrap, input: target.input, msg: msg, step: target.step });
        else leftovers.push(msg);
      });
      return { fields: out, leftovers: leftovers };
    }

    /* ---------- checkout ---------- */
    function buildBody() {
      var recipientLang = state.injected.recipientLang ? val(state.injected.recipientLang) : '';
      if (!recipientLang) recipientLang = (state.config && state.config.defaultLanguage) || env.locale;
      var deliverAt = null;
      if (state.dateWrap && deliveryLater()) deliverAt = val(state.injected.deliverAt) || null;
      var sender = fieldMode('senderName') === 'hidden' ? '' : val(els.sender);
      var phoneHidden = channelMode('recipientPhone') === 'hidden';
      /* the digits only ("0528048064"): the app normalises to +972 */
      var phone = phoneHidden ? '' : (phoneDigits(val(els.phone)) || val(els.phone));
      var confirm = phoneHidden || !els.phoneConfirm ? '' : (phoneDigits(val(els.phoneConfirm)) || val(els.phoneConfirm));
      return {
        variantId: state.selected ? state.selected.variantId : '',
        numericVariantId: state.selected ? state.selected.numericVariantId : '',
        recipientName: val(els.recipient),
        senderName: sender,
        deliveryChannel: channel(),
        recipientEmail: channelMode('recipientEmail') === 'hidden' || !state.injected.recipientEmail ? '' : val(state.injected.recipientEmail),
        recipientPhone: phone,
        recipientPhoneConfirm: confirm,
        recipientLang: recipientLang,
        occasion: state.injected.occasion ? val(state.injected.occasion) : '',
        greeting: els.blessing ? els.blessing.value.slice(0, state.greetingMax) : '',
        deliverAt: deliverAt,
        buyerEmail: state.injected.buyerEmail ? val(state.injected.buyerEmail) : '',
        buyerName: sender,
        lang: env.locale
      };
    }

    function handleSoldOut() {
      var sel = state.selected;
      if (sel) {
        state.soldOutLocal[chipKey(sel)] = true;
        qa('button[data-amount]', els.amounts).forEach(function (b) {
          if (b.dataset.mygiftKey === chipKey(sel)) markSoldOut(b);
        });
      }
      state.selected = null;
      state.autoSelect = false;
      clearErrors();
      goToStep(2);
      loadStock().then(function (ok) {
        if (!ok) showStatus(t('connectionErrorText'), loadAll);
      });
    }

    function submit() {
      if (state.inFlight) return;
      clearErrors();
      hideStatus();
      if (!state.config || !state.stock) {
        loadAll().then(function (ok) { if (ok && !(state.config && state.config.sellingPaused)) submit(); });
        return;
      }
      var errs = validate('all');
      if (errs.length) { showErrors(errs); return; }

      var body = buildBody();
      setBusy(true);
      fetchJson(env.proxy + '/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        if (res.ok && res.data && typeof res.data === 'object' && typeof res.data.checkoutUrl === 'string') {
          var url = safeCheckoutUrl(res.data.checkoutUrl);
          if (url) { location.assign(url); return; }
          console.warn('[mygift-bridge] checkout url rejected (not https or foreign host)');
          setBusy(false);
          showStatus(t('connectionErrorText'), submit);
          return;
        }
        setBusy(false);
        if (res.status === 409) { handleSoldOut(); return; }
        if (res.status === 400 && res.data && res.data.errors && typeof res.data.errors === 'object') {
          var mapped = mapServerErrors(res.data.errors);
          if (mapped.fields.length) showErrors(mapped.fields);
          if (mapped.leftovers.length) showStatus(mapped.leftovers.join(' '), null);
          if (mapped.fields.length || mapped.leftovers.length) return;
        }
        console.warn('[mygift-bridge] checkout failed', res.status, res.error || '');
        showStatus(t('connectionErrorText'), submit);
      });
    }

    /* Capture phase on the actions container: runs before the builder's own click handler on the
       button, so an invalid step never advances and step 3 never shows the builder's fake success. */
    els.actions.addEventListener('click', function (e) {
      if (!els.cta.contains(e.target)) return;
      if (mode() !== 'personal') return;
      if (state.inFlight) { e.stopPropagation(); e.preventDefault(); return; }
      var s = step();
      if (s === 3) {
        e.stopPropagation();
        e.preventDefault();
        submit();
        return;
      }
      if (!state.config) return;
      var errs = validate(s === 1 ? 'p1' : 'p2');
      if (errs.length) {
        e.stopPropagation();
        e.preventDefault();
        showErrors(errs);
      }
    }, true);

    /* Editing any field clears that field's inline error and the connection-error box, and refreshes the ticks. */
    function onFieldEdit(e) {
      var wrap = e.target && e.target.closest ? e.target.closest('[data-mygift-field], .mygift-message') : null;
      if (wrap) {
        setError(wrap, '');
        hideStatus();
      }
      refreshValidity();
    }
    root.addEventListener('input', onFieldEdit);
    root.addEventListener('change', onFieldEdit);
    if (els.customInput) els.customInput.addEventListener('input', onCustomInput);
    bindPhones();
    refreshValidity();

    /* ---------- loading ---------- */
    function applyConfig(data) {
      state.config = data;
      state.texts = data.texts && typeof data.texts === 'object' ? data.texts : {};
      state.fields = data.fields && typeof data.fields === 'object' ? data.fields : {};
      var max = Number(data.greetingMaxLength);
      state.greetingMax = isFinite(max) && max > 0 ? Math.floor(max) : 180;

      if (data.sellingPaused) { renderPaused(); return; }

      if (els.groupTab) {
        els.groupTab.hidden = data.hideGroupTab !== false;
        if (els.modes) els.modes.classList.toggle('mygift-bridge-single', els.groupTab.hidden);
      }
      if (els.customWrap) els.customWrap.hidden = data.hideCustomAmount !== false;
      if (els.blessing) els.blessing.maxLength = state.greetingMax;

      injectFields();
      renderLines();
      renderStoreOnly();
      observe();
      syncView();
    }

    function loadStock() {
      return fetchJson(env.proxy + '/stock').then(function (res) {
        if (!res.ok || !res.data || !Array.isArray(res.data.denominations)) {
          console.warn('[mygift-bridge] stock unavailable', res.status, res.error || '');
          return false;
        }
        state.stock = res.data;
        renderAmounts();
        return true;
      });
    }

    function loadAll() {
      hideStatus();
      var cfgUrl = env.proxy + '/config?lang=' + encodeURIComponent(env.locale);
      return Promise.all([fetchJson(cfgUrl), fetchJson(env.proxy + '/stock')]).then(function (r) {
        var cfg = r[0];
        var stk = r[1];
        if (!cfg.ok || !cfg.data || typeof cfg.data !== 'object') {
          console.warn('[mygift-bridge] config unavailable', cfg.status, cfg.error || '');
          showStatus(t('connectionErrorText'), loadAll);
          return false;
        }
        applyConfig(cfg.data);
        if (cfg.data.sellingPaused) return true;
        if (!stk.ok || !stk.data || !Array.isArray(stk.data.denominations)) {
          console.warn('[mygift-bridge] stock unavailable', stk.status, stk.error || '');
          showStatus(t('connectionErrorText'), loadAll);
          return false;
        }
        state.stock = stk.data;
        renderAmounts();
        return true;
      });
    }

    loadAll();
  }

  /* The builder marks its root with data-mygift-init once its handlers are bound. Wait for it. */
  function whenBuilderReady(root, tries) {
    if (root.dataset.mygiftInit === 'true' || tries >= BUILDER_WAIT_TRIES) {
      if (root.dataset.mygiftInit !== 'true') console.warn('[mygift-bridge] builder did not initialise, attaching anyway');
      initBridge(root);
      return;
    }
    setTimeout(function () { whenBuilderReady(root, tries + 1); }, 50);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-mygift-builder]').forEach(function (root) { whenBuilderReady(root, 0); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelectorAll) initAll(e.target);
  });
})();
